import { useState } from 'react';
import { format } from 'date-fns';
import { ToolLayout } from '../../components/tools/ToolLayout';
import { MiniLineChart } from '../../components/tools/MiniLineChart';
import { SUPPORTED_COINS, formatBrl } from '../../data/coins';

type Frequency = 'semanal' | 'quinzenal' | 'mensal';

interface DcaResult {
  totalInvested: number;
  totalQty: number;
  currentValue: number;
  profit: number;
  profitPct: number;
  avgPrice: number;
  currentPrice: number;
  purchases: number;
  chartValues: number[];
  chartInvested: number[];
  chartLabels: string[];
  updatedAt: Date;
}

const FREQUENCY_DAYS: Record<Frequency, number> = { semanal: 7, quinzenal: 14, mensal: 30 };

const isoDaysAgo = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

export const CalculadoraDcaPage: React.FC = () => {
  const [coinId, setCoinId] = useState('bitcoin');
  const [amount, setAmount] = useState('100');
  const [frequency, setFrequency] = useState<Frequency>('mensal');
  const [startDate, setStartDate] = useState(isoDaysAgo(364));
  const [endDate, setEndDate] = useState(isoDaysAgo(0));
  const [result, setResult] = useState<DcaResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const calculate = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setResult(null);

    const amountNum = parseFloat(amount.replace(',', '.'));
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      setError('Informe um valor recorrente válido em reais.');
      return;
    }
    const start = new Date(`${startDate}T12:00:00`);
    const end = new Date(`${endDate}T12:00:00`);
    if (!(start < end)) {
      setError('A data inicial deve ser anterior à data final.');
      return;
    }
    if (Date.now() - start.getTime() > 365 * 24 * 60 * 60 * 1000) {
      setError('O histórico gratuito da CoinGecko cobre os últimos 12 meses — escolha uma data inicial dentro desse período.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(
        `https://api.coingecko.com/api/v3/coins/${coinId}/market_chart?vs_currency=brl&days=365&interval=daily`
      );
      const data = await res.json();
      const prices: [number, number][] = data?.prices;
      if (!Array.isArray(prices) || prices.length < 2) {
        throw new Error('sem dados');
      }

      // Índice por dia (YYYY-MM-DD) → preço de fechamento
      const priceByDay = new Map<string, number>();
      for (const [ts, price] of prices) {
        priceByDay.set(new Date(ts).toISOString().slice(0, 10), price);
      }

      const stepDays = FREQUENCY_DAYS[frequency];
      let invested = 0;
      let qty = 0;
      let purchases = 0;
      const chartValues: number[] = [];
      const chartInvested: number[] = [];
      const chartLabels: string[] = [];

      const findPrice = (day: string): number | undefined => {
        // usa o dia exato ou o mais próximo anterior (até 5 dias)
        const date = new Date(`${day}T12:00:00`);
        for (let i = 0; i < 6; i++) {
          const key = new Date(date.getTime() - i * 86400000).toISOString().slice(0, 10);
          const price = priceByDay.get(key);
          if (price) return price;
        }
        return undefined;
      };

      for (let t = start.getTime(); t <= end.getTime(); t += stepDays * 86400000) {
        const day = new Date(t).toISOString().slice(0, 10);
        const price = findPrice(day);
        if (!price) continue;
        invested += amountNum;
        qty += amountNum / price;
        purchases++;
        chartValues.push(qty * price);
        chartInvested.push(invested);
        chartLabels.push(format(new Date(t), 'dd/MM/yy'));
      }

      if (purchases === 0) {
        throw new Error('sem compras no período');
      }

      const currentPrice = prices[prices.length - 1][1];
      const currentValue = qty * currentPrice;

      setResult({
        totalInvested: invested,
        totalQty: qty,
        currentValue,
        profit: currentValue - invested,
        profitPct: ((currentValue - invested) / invested) * 100,
        avgPrice: invested / qty,
        currentPrice,
        purchases,
        chartValues,
        chartInvested,
        chartLabels,
        updatedAt: new Date(),
      });
    } catch {
      setError('Não foi possível obter o histórico de preços agora. Tente novamente em instantes.');
    } finally {
      setLoading(false);
    }
  };

  const coin = SUPPORTED_COINS.find((c) => c.id === coinId)!;

  return (
    <ToolLayout
      path="/ferramentas/calculadora-dca"
      title="Calculadora DCA"
      metaTitle="Calculadora DCA de Criptomoedas em Reais"
      metaDescription="Simule o resultado histórico de aportes recorrentes (dollar-cost averaging) em Bitcoin, Ethereum e outras criptomoedas, em reais, com gráfico de evolução, preço médio e retorno."
      intro={[
        'DCA (dollar-cost averaging, ou "preço médio") é a estratégia de investir um valor fixo em intervalos regulares, independentemente do preço do ativo. Em vez de tentar acertar o melhor momento de compra, o investidor dilui o preço de entrada ao longo do tempo.',
        'Esta calculadora simula quanto teria rendido um plano de aportes recorrentes em criptomoedas nos últimos 12 meses, usando preços históricos reais em reais (BRL) da CoinGecko. O cálculo é retrospectivo: mostra o que aconteceu, não o que vai acontecer.',
      ]}
      methodology={[
        'Preços históricos diários em BRL fornecidos pela API pública da CoinGecko (últimos 12 meses).',
        'Cada aporte é simulado pelo preço de fechamento do dia da compra (ou do dia útil de dados mais próximo).',
        'O valor atual usa o último preço disponível na série histórica.',
        'Taxas de corretagem, spread e impostos não são considerados — os resultados reais tendem a ser ligeiramente menores.',
        'Preço médio = total investido ÷ quantidade acumulada.',
      ]}
      faqs={[
        {
          question: 'O que é DCA (dollar-cost averaging)?',
          answer:
            'É a estratégia de investir um valor fixo em intervalos regulares (por exemplo, R$ 100 todo mês), sem tentar prever o mercado. Com o tempo, compra-se mais unidades quando o preço está baixo e menos quando está alto, formando um preço médio.',
        },
        {
          question: 'A calculadora prevê resultados futuros?',
          answer:
            'Não. A simulação é exclusivamente histórica: mostra o que teria acontecido com aportes no passado. Resultados passados não garantem resultados futuros, especialmente em ativos voláteis como criptomoedas.',
        },
        {
          question: 'Por que o resultado real pode ser diferente?',
          answer:
            'A simulação não inclui taxas de corretagem, spread entre preço de compra e venda, nem impostos. Cada exchange também pratica preços ligeiramente diferentes do preço médio de mercado usado aqui.',
        },
        {
          question: 'Qual período posso simular?',
          answer:
            'Até 12 meses para trás, que é o histórico disponível na API gratuita da CoinGecko utilizada pela ferramenta.',
        },
      ]}
    >
      <div className="bg-white rounded-xl shadow p-6 md:p-8 mb-6">
        <form onSubmit={calculate} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
          <label className="block">
            <span className="text-sm font-semibold text-gray-700 mb-1 block">Ativo</span>
            <select
              value={coinId}
              onChange={(e) => setCoinId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
            >
              {SUPPORTED_COINS.map((c) => (
                <option key={c.id} value={c.id}>{c.name} ({c.symbol})</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-gray-700 mb-1 block">Aporte (R$)</span>
            <input
              type="number"
              min="1"
              step="any"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
              required
            />
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-gray-700 mb-1 block">Frequência</span>
            <select
              value={frequency}
              onChange={(e) => setFrequency(e.target.value as Frequency)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
            >
              <option value="semanal">Semanal</option>
              <option value="quinzenal">Quinzenal</option>
              <option value="mensal">Mensal</option>
            </select>
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-gray-700 mb-1 block">Data inicial</span>
            <input
              type="date"
              value={startDate}
              min={isoDaysAgo(364)}
              max={endDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
              required
            />
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-gray-700 mb-1 block">Data final</span>
            <input
              type="date"
              value={endDate}
              min={startDate}
              max={isoDaysAgo(0)}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
              required
            />
          </label>
          <button
            type="submit"
            disabled={loading}
            className="sm:col-span-2 lg:col-span-5 bg-blue-600 text-white font-semibold rounded-lg px-6 py-2.5 hover:bg-blue-700 transition disabled:opacity-60"
          >
            {loading ? 'Calculando...' : 'Calcular'}
          </button>
        </form>

        {error && (
          <p className="mt-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">{error}</p>
        )}

        {result && (
          <div className="mt-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              {[
                { label: 'Total investido', value: formatBrl(result.totalInvested) },
                { label: 'Valor atual', value: formatBrl(result.currentValue) },
                {
                  label: 'Resultado',
                  value: `${formatBrl(result.profit)} (${result.profitPct >= 0 ? '+' : ''}${result.profitPct.toFixed(1)}%)`,
                  colored: true,
                  positive: result.profit >= 0,
                },
                { label: `Quantidade (${coin.symbol})`, value: result.totalQty.toLocaleString('pt-BR', { maximumFractionDigits: 8 }) },
                { label: 'Preço médio', value: formatBrl(result.avgPrice, 2) },
                { label: 'Aportes realizados', value: String(result.purchases) },
              ].map((stat) => (
                <div key={stat.label} className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-1">{stat.label}</p>
                  <p
                    className={`font-bold text-sm md:text-base ${
                      'colored' in stat && stat.colored
                        ? stat.positive ? 'text-green-600' : 'text-red-600'
                        : 'text-gray-900'
                    }`}
                  >
                    {stat.value}
                  </p>
                </div>
              ))}
            </div>

            <MiniLineChart
              series={[
                { label: 'Valor da carteira', values: result.chartValues, color: '#2563eb' },
                { label: 'Total investido', values: result.chartInvested, color: '#9ca3af' },
              ]}
              xLabels={result.chartLabels}
              formatValue={(v) => formatBrl(v)}
            />
            <p className="text-xs text-gray-400 mt-3">
              Fonte dos preços: CoinGecko · consulta em{' '}
              {result.updatedAt.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}.
              Simulação histórica — não representa garantia de resultado futuro.
            </p>
          </div>
        )}
      </div>
    </ToolLayout>
  );
};
